import React, { useState } from 'react';
import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, Link, useBCMSContext, useGlobalController, useModel, useParams, useRoute, useStateManager, useWorkflow, useWorkflowStep } from "bcms-react";

import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, LayoutContainer, PageContainer, PageContentView, WidgetContainer } from 'components/shared';
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import Classes, { Profile } from "model";
import ExternalIframe from 'components/shared/containers/ExternalIframe';
import { PrimaryButton } from 'components/ui';
import Util from 'classes/Util';
import "./styles.css";
import "./mobile.css";

/**
 * @function ChangeProfilePage
 * @return {React.Component}
 * @exported true
 * @description The change profile page
 */

export default function ChangeProfilePage(props) {
  const navigate = useRoute();
  const { user } = useModel();
  const { profileID, profileType } = useParams();
  // Branding options needed for captures
  const branding = useModel().branding.options;
  window.BRANDING = { "zle_abi22" : branding.zle_abi22 };
  // Initalize all model classes
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  // Sets the values for the data and ui variables
  const data = { ...getDefaultPageData(profileID, profileType), ...props.data ?? {} };
  const ui = { ...getDefaultPageUI(data.transfer), ...props.ui ?? {} };
  // The state to be passed to the state manager
  const state = { data, ui };
  
  // :: React.state, React.setState -> OBJECT
  // Function to initalize the controller for the state manager
  const controller = (state, setState) => ({
      data: state.data, // Set the data property of the controller
      ui: state.ui, // Set the ui property of the controller
      
      // Event listeners.
      onChange: profileID => {
        const selectedProfile = Profile.initByID(profileID);

        BCMSStateManagerCRUD.init(state)
        .update([ "data", "profileType" ], selectedProfile.type)
        .update([ "data", "profileID" ], selectedProfile.id)
        .submit(setState);
      },

      onSelectClick: () => {
        // If we are in abi22 standalone, updated the zelle iframe.
        if(data.showZLEIframe) {
          // Update the profile type in the url. 
          // This url in abi22 will be read by the zle iframe in order to 
          // determine whether to show personal or business functionality.
          navigate(`/changeProfile/${ data.profileType }/${ data.profileID }`);

          // Reload the zelle iframe.
          const zleIframe = document.querySelector(".zelle-iframe");
          zleIframe.src = zleIframe.src;
        // Else, we are in mabi23, so navigate to the zelle page.
        } else {
          navigate(`/zelle/${ data.profileType }`);
        }
      }
  });

  /* Page body is removed for specific captures, so this class is used to do that. */
  const noBackgroundClass = ui.hidePageBody ? "no-background" : "";
  
  return(
    <PageContainer className={ `change-profile-page ${ noBackgroundClass }` } state={ state } controller={ controller }>
        <PageContent/>
    </PageContainer>
  );
}

/**
 * @name getDefaultPageData
 * @param {Void} 
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (profileID, profileType) => {
  const profiles = Profile.initAll();
  const inDesktopView = Util.inDesktopView;
  const inMabi = Util.inMabi;

  return {
      // The zelle iframe is shown on this page in abi22 standalone only.
      showZLEIframe: !inMabi,
      profiles,
      profileID: profileID ?? "0",
      profileType: profileType ?? "personal"
  }
};

/**
* @name getDefaultPageUI
* @param {Void} 
* @return {OBJECT}
* @description Hook to return the default ui required by the page.
*/
export const getDefaultPageUI = (currentProfileSelectIsActive, showHeader, hidePageBody) => ({
  // no ui changes for now
  currentProfileSelectIsActive,
  hidePageBody: hidePageBody ?? false,
  showHeader: showHeader ?? true,
});

function PageContent(props) {
  const { data, ui, onChange, onSelectClick } = useStateManager();
  const { profileID } = data;

  const profiles = Profile.initAll(); 
  console.log(profiles)

  return (
    <PageContentView showHeader={ ui.showHeader } hidePageBody={ ui.hidePageBody }>
      <div className="change-profile-page-container flex-container flex-vertical flex-width">
       <ProfilePicker ui={ui} profiles={profiles} profileID ={profileID}  onChange = {onChange} onSelectClick={onSelectClick}/>
        {/* ZLE iframe is only shown in desktop view. */}
        { data.showZLEIframe &&
        <WidgetContainer className="zelle-widget-container">
          <div className="iframe-container flex-container flex-horizontal justify-center">
            {/* iFrame for Zelle goes here */}
            <ExternalIframe className="zelle-iframe" demo="ZLE" key="ChangeProfilePage"/>      
          </div>
        </WidgetContainer> 
        }
      </div>
    </PageContentView>
  );
}
/**
* @name ProfilePicker
* @param {Void} 
* @return {OBJECT}
* @description Component to return set a selected profile for the iframe.
*/
export function ProfilePicker(props){
 

  const profileID =props.profileID ?? "0";
  const profiles = props.profiles;

 const {ui,onChange,onSelectClick,disableBusiness} = props;
 const items = profiles.reduce((acc, profile) => {
 
  acc.push(
    disableBusiness === "true" ?
      profile.type === "business"?
          <DisabledPrompt>
            <CustomSelectItem key={ profile.id } value={ profile.id } dataPoint={ `Current_Profile_Select_Item_${ profile.id }` }>{ profile.name }</CustomSelectItem>
          </DisabledPrompt>:
          <CustomSelectItem key={ profile.id } value={ profile.id } dataPoint={ `Current_Profile_Select_Item_${ profile.id }` }>{ profile.name }</CustomSelectItem>:
      <CustomSelectItem key={ profile.id } value={ profile.id } dataPoint={ `Current_Profile_Select_Item_${ profile.id }` }>{ profile.name }</CustomSelectItem>
  );
  return acc;
}, []);
return  <WidgetContainer className="change-profile-widget">
          <ContentHeaderView text="Change Current Profile" className="show-on-desktop" />
          <FormContainer className="small-form-width">
            <FormRowView className="profile-select-form-row" text="Current Profile">
              <CustomSelect active={ ui.currentProfileSelectIsActive } value={ profileID } onChange={ onChange } dataPoint="Current_Profile_Select">{ items }</CustomSelect>
            </FormRowView>
            <PrimaryButton className="change-profile-button" text="Select" dataPoint="Select_Button" onClick={ onSelectClick }/>
          </FormContainer>
          </WidgetContainer>
}
